<script lang="ts" setup>
import type { HTMLAttributes } from 'vue';
import { reactiveOmit } from '@vueuse/core';
import {
  RangeCalendarRoot,
  type RangeCalendarRootEmits,
  type RangeCalendarRootProps,
  useForwardPropsEmits,
} from 'reka-ui';
import { cn } from '@/lib/utils';
import {
  RangeCalendarCell,
  RangeCalendarCellTrigger,
  RangeCalendarGrid,
  RangeCalendarGridBody,
  RangeCalendarGridHead,
  RangeCalendarGridRow,
  RangeCalendarHeadCell,
  RangeCalendarHeader,
  RangeCalendarHeading,
  RangeCalendarNextButton,
  RangeCalendarPrevButton,
} from '.';
import type { DateValue } from '@internationalized/date';
import { getLocalTimeZone } from '@internationalized/date';
import { ref } from 'vue';

const props = defineProps<RangeCalendarRootProps & { class?: HTMLAttributes['class'] }>();

const emits = defineEmits<RangeCalendarRootEmits>();

const delegatedProps = reactiveOmit(props, 'class');

const forwarded = useForwardPropsEmits(delegatedProps, emits);
const startValue = ref<DateValue | undefined>(undefined);
const hoveredDate = ref<DateValue | undefined>(undefined);

const handleStartValueUpdate = (date: any) => {
  startValue.value = date;
};

const handleCellHover = (date: DateValue) => {
  hoveredDate.value = date;
};

const handleCellLeave = () => {
  hoveredDate.value = undefined;
};

const isBetweenStartAndHovered = (date: DateValue) => {
  // Only highlight when we have a start date and are hovering over a potential end date
  // This should only happen when the range is incomplete (no end date selected yet)
  if (!startValue.value || !hoveredDate.value) {
    return false;
  }

  // Convert to comparable Date objects using local timezone
  const localTimeZone = getLocalTimeZone();
  const dateValue = date.toDate(localTimeZone);
  const startDateValue = startValue.value.toDate(localTimeZone);
  const hoveredDateValue = hoveredDate.value.toDate(localTimeZone);

  // Ensure we're comparing in the right order (start should be before end)
  const earlierDate = startDateValue < hoveredDateValue ? startDateValue : hoveredDateValue;
  const laterDate = startDateValue < hoveredDateValue ? hoveredDateValue : startDateValue;

  // Check if date is between start and hovered (exclusive of endpoints)
  return dateValue > earlierDate && dateValue < laterDate;
};
</script>

<template>
  <RangeCalendarRoot
    v-slot="{ grid, weekDays }"
    :class="cn('tw-p-3', props.class)"
    v-bind="forwarded"
    :week-starts-on="1"
    @update:start-value="handleStartValueUpdate"
  >
    <RangeCalendarHeader>
      <RangeCalendarPrevButton />
      <RangeCalendarHeading />
      <RangeCalendarNextButton />
    </RangeCalendarHeader>

    <div class="tw-flex tw-flex-col tw-gap-y-4 tw-mt-4 sm:tw-flex-row sm:tw-gap-x-4 sm:tw-gap-y-0">
      <RangeCalendarGrid v-for="month in grid" :key="month.value.toString()">
        <RangeCalendarGridHead>
          <RangeCalendarGridRow>
            <RangeCalendarHeadCell v-for="day in weekDays" :key="day">
              {{ day }}
            </RangeCalendarHeadCell>
          </RangeCalendarGridRow>
        </RangeCalendarGridHead>
        <RangeCalendarGridBody>
          <RangeCalendarGridRow
            v-for="(weekDates, index) in month.rows"
            :key="`weekDate-${index}`"
            class="tw-mt-2 tw-w-full"
          >
            <RangeCalendarCell
              v-for="weekDate in weekDates"
              :key="weekDate.toString()"
              :date="weekDate"
              :class="
                isBetweenStartAndHovered(weekDate)
                  ? 'tw-bg-gray-50 hover:tw-bg-gray-100 tw-transition-colors'
                  : ''
              "
              @mouseenter="handleCellHover(weekDate)"
              @mouseleave="handleCellLeave"
            >
              <RangeCalendarCellTrigger :day="weekDate" :month="month.value" />
            </RangeCalendarCell>
          </RangeCalendarGridRow>
        </RangeCalendarGridBody>
      </RangeCalendarGrid>
    </div>
  </RangeCalendarRoot>
</template>
